import { useColorMode } from "@chakra-ui/react";
import { GroupBase, StylesConfig, Theme } from "react-select";

import { useTheme as useChakraTheme } from "../components";

// react-select types are complex and difficult to use in a wrapper.
// useSelectTheme returns custom theme and style props.
// see https://react-select.com/styles
const useSelectTheme = (
  styles?: StylesConfig
): [(theme: Theme) => Theme, Record<string, any>] => {
  const chakraTheme = useChakraTheme();
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";

  const getNestedValue = (
    obj: Record<string, any> | undefined,
    path: string[]
  ): any => {
    return path.reduce((acc: any, key: string) => {
      if (!acc || typeof acc !== "object") return undefined;
      return acc[key];
    }, obj);
  };

  const getColorPair = (
    tokenPath: string,
    fallback?: string
  ): [string, string] => {
    const segments = tokenPath.split(".");
    const semantic = getNestedValue(
      chakraTheme.semanticTokens?.colors as Record<string, any>,
      segments
    ) as { default?: string; _dark?: string } | undefined;
    const base = getNestedValue(
      chakraTheme.colors as Record<string, any>,
      segments
    ) as string | undefined;

    const lightValue =
      (typeof semantic?.default === "string" && semantic?.default) ||
      base ||
      fallback ||
      tokenPath;
    const semanticDark =
      semantic &&
      // eslint-disable-next-line no-underscore-dangle
      typeof semantic._dark === "string"
        ? // eslint-disable-next-line no-underscore-dangle
          semantic._dark
        : undefined;
    const darkValue = semanticDark || lightValue;

    return [lightValue, darkValue];
  };

  const [controlBackgroundLight, controlBackgroundDark] = getColorPair(
    "surfaceInteractive",
    chakraTheme.colors?.gray?.[50]
  );
  const [menuBackgroundLight, menuBackgroundDark] = getColorPair(
    "surfaceMenu",
    chakraTheme.colors?.white ?? "white"
  );
  const [borderLight, borderDark] = getColorPair(
    "borderSubtle",
    chakraTheme.colors?.gray?.[200]
  );
  const [primaryLight, primaryDark] = getColorPair(
    "textPrimary",
    chakraTheme.colors?.gray?.[800]
  );
  const [secondaryLight, secondaryDark] = getColorPair(
    "textMeta",
    chakraTheme.colors?.gray?.[400]
  );
  const [placeholderLight, placeholderDark] = getColorPair(
    "textPlaceholderDefault",
    chakraTheme.colors?.gray?.[600]
  );
  const [accentLight, accentDark] = getColorPair(
    "selectAccent",
    chakraTheme.colors?.blue?.[500]
  );
  const [optionHoverLight, optionHoverDark] = getColorPair(
    "surfaceMenuHover",
    chakraTheme.colors?.gray?.[100]
  );
  const [optionActiveLight, optionActiveDark] = getColorPair(
    "surfaceMenuActive",
    chakraTheme.colors?.gray?.[200]
  );
  const [infoBackgroundLight, infoBackgroundDark] = getColorPair(
    "selectMultiValueBg",
    chakraTheme.colors?.blue?.[50] ?? "rgba(40, 153, 245, 0.18)"
  );

  const toCssVarToken = (tokenPath: string): string =>
    tokenPath.replace(/\./g, "-");

  const semanticColorValue = (
    tokenPath: string,
    lightValue: string,
    darkValue: string
  ): string => {
    const cssVar = toCssVarToken(tokenPath);
    const fallback = isDark ? darkValue : lightValue;
    return `var(--chakra-colors-${cssVar}, ${fallback})`;
  };

  const controlBackground = semanticColorValue(
    "surfaceInteractive",
    controlBackgroundLight,
    controlBackgroundDark
  );
  const borderColor = semanticColorValue(
    "borderSubtle",
    borderLight,
    borderDark
  );
  const menuBackground = semanticColorValue(
    "surfaceMenu",
    menuBackgroundLight,
    menuBackgroundDark
  );
  const textPrimary = semanticColorValue(
    "textPrimary",
    primaryLight,
    primaryDark
  );
  const textSecondary = semanticColorValue(
    "textMeta",
    secondaryLight,
    secondaryDark
  );
  const placeholderColor = semanticColorValue(
    "textPlaceholderDefault",
    placeholderLight,
    placeholderDark
  );
  const accent = semanticColorValue("selectAccent", accentLight, accentDark);
  const optionHover = semanticColorValue(
    "surfaceInteractiveHover",
    optionHoverLight,
    optionHoverDark
  );
  const optionActive = semanticColorValue(
    "surfaceInteractiveActive",
    optionActiveLight,
    optionActiveDark
  );
  const infoBackground = semanticColorValue(
    "selectMultiValueBg",
    infoBackgroundLight,
    infoBackgroundDark
  );

  const lightColorOverrides = {
    text: chakraTheme.primary,
    neutral: chakraTheme.primary,
    neutral20: chakraTheme.colors.gray[300],
    neutral50: chakraTheme.colors.gray[400],
    neutral75: chakraTheme.colors.gray[500],
    primary25: chakraTheme.colors.blue[50],
    primary50: chakraTheme.colors.blue[300],
    primary75: chakraTheme.colors.blue[400],
    primary: chakraTheme.colors.blue[500],
  };

  const darkColorOverrides = {
    text: textPrimary,
    neutral: textPrimary,
    neutral0: controlBackground,
    neutral5: controlBackground,
    neutral10: controlBackground,
    neutral20: borderColor,
    neutral30: borderColor,
    neutral40: borderColor,
    neutral50: placeholderColor,
    neutral60: textSecondary,
    neutral70: textSecondary,
    neutral80: textPrimary,
    neutral90: textPrimary,
    primary25: optionHover,
    primary50: optionActive,
    primary75: accent,
    primary: accent,
  };

  const lightStyles = {
    menu: (provided: Record<string, any>) => ({
      ...provided,
      zIndex: 3,
      backgroundColor: menuBackground,
    }),
    menuPortal: (provided: Record<string, any>) => ({
      ...provided,
      zIndex: 1500,
    }),
    control: (provided: Record<string, any>) => ({
      ...provided,
      minHeight: "40px",
      borderRadius: chakraTheme.radii.sm,
    }),
    multiValue: (provided: Record<string, any>) => ({
      ...provided,
      backgroundColor: chakraTheme.colors.blue[50],
    }),
    multiValueLabel: (provided: Record<string, any>) => ({
      ...provided,
      color: chakraTheme.colors.blue[700],
    }),
    multiValueRemove: (provided: Record<string, any>) => ({
      ...provided,
      color: chakraTheme.colors.blue[300],
      ":hover": {
        backgroundColor: chakraTheme.colors.blue[300],
        color: "white",
      },
    }),
  } as Record<string, any>;

  const darkStyles = {
    menu: (provided: Record<string, any>) => ({
      ...provided,
      zIndex: 3,
      backgroundColor: menuBackground,
      color: textPrimary,
      border: `1px solid ${borderColor}`,
      boxShadow: chakraTheme.shadows?.elevate ?? provided.boxShadow,
    }),
    menuPortal: (provided: Record<string, any>) => ({
      ...provided,
      zIndex: 1500,
    }),
    control: (provided: Record<string, any>, state: Record<string, any>) => ({
      ...provided,
      minHeight: "40px",
      borderRadius: chakraTheme.radii?.sm ?? provided.borderRadius,
      backgroundColor: controlBackground,
      borderColor,
      color: textPrimary,
      boxShadow: state.isFocused ? `0 0 0 1px ${accent}` : "none",
      ":hover": {
        borderColor,
      },
    }),
    valueContainer: (provided: Record<string, any>) => ({
      ...provided,
      color: textPrimary,
    }),
    placeholder: (provided: Record<string, any>) => ({
      ...provided,
      color: placeholderColor,
    }),
    singleValue: (provided: Record<string, any>) => ({
      ...provided,
      color: textPrimary,
    }),
    input: (provided: Record<string, any>) => ({
      ...provided,
      color: textPrimary,
    }),
    dropdownIndicator: (
      provided: Record<string, any>,
      state: Record<string, any>
    ) => ({
      ...provided,
      color: state.isFocused ? accent : placeholderColor,
      ":hover": {
        color: accent,
      },
    }),
    clearIndicator: (provided: Record<string, any>) => ({
      ...provided,
      color: placeholderColor,
      ":hover": {
        color: accent,
      },
    }),
    indicatorSeparator: (provided: Record<string, any>) => ({
      ...provided,
      backgroundColor: borderColor,
    }),
    menuList: (provided: Record<string, any>) => ({
      ...provided,
      backgroundColor: menuBackground,
      color: textPrimary,
    }),
    option: (provided: Record<string, any>, state: Record<string, any>) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? optionActive
        : state.isFocused
        ? optionHover
        : menuBackground,
      color: textPrimary,
      ":active": {
        backgroundColor: optionActive,
      },
    }),
    multiValue: (provided: Record<string, any>) => ({
      ...provided,
      backgroundColor: infoBackground,
      borderRadius: chakraTheme.radii?.sm ?? provided.borderRadius,
    }),
    multiValueLabel: (provided: Record<string, any>) => ({
      ...provided,
      color: textPrimary,
    }),
    multiValueRemove: (provided: Record<string, any>) => ({
      ...provided,
      color: textPrimary,
      ":hover": {
        backgroundColor: optionHover,
        color: textPrimary,
      },
    }),
    noOptionsMessage: (provided: Record<string, any>) => ({
      ...provided,
      backgroundColor: controlBackground,
      color: textSecondary,
    }),
    loadingMessage: (provided: Record<string, any>) => ({
      ...provided,
      backgroundColor: controlBackground,
      color: textSecondary,
    }),
    groupHeading: (provided: Record<string, any>) => ({
      ...provided,
      color: textSecondary,
    }),
  } as Record<string, any>;

  return [
    (provided) => ({
      ...provided,
      colors: {
        ...provided.colors,
        ...(isDark ? darkColorOverrides : lightColorOverrides),
      },
    }),
    {
      ...(isDark ? darkStyles : lightStyles),
      ...styles,
    },
  ];
};

/**
 * Identical to `useSelectTheme`, but with typed styles. Useful for type hints
 * in the provided styles
 */
export const useTypedSelectTheme = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  styles?: StylesConfig<Option, IsMulti, Group>
): [(theme: Theme) => Theme, StylesConfig<Option, IsMulti, Group>] => {
  return useSelectTheme(styles as unknown as StylesConfig);
};

export default useSelectTheme;
